import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { IconClose } from './Icons';
import {
  COLORE_CAMPAGNE, statoAgente, motivoAgente, fmtEta, fmtVelocita, fmtResaAgente,
} from './campagneAgenteData';
import './phases.css';

const nf = (n) => (n ?? 0).toLocaleString('it-IT');

export const IconCampagne = ({ size = 22, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" style={style}>
    {/* un foglio d'elenco con la lente sopra: l'indagine sulle liste (la lente sola e' di Indaga) */}
    <path d="M11.5 20.5H6.2a2.2 2.2 0 0 1-2.2-2.2V5.7a2.2 2.2 0 0 1 2.2-2.2h7.3l4 4v2.8" />
    <path d="M8 8.5h4.5" /><path d="M8 12h3" /><path d="M8 15.5h1.8" />
    <circle cx="16" cy="15.8" r="3.4" /><path d="m18.6 18.4 2.4 2.4" />
  </svg>
);

/* Finestra dell'agente Campagne (dalla spia nello sportellino): stato, riga in lavorazione,
   i numeri delle ultime 24 ore letti dal database, e il motivo in chiaro quando aspetta un
   captcha o e' fermo per errori. Gemella di IndagaStatsDialog, con i dati di
   /campagne/agente. */
export default function CampagneStatsDialog({ open, onClose }) {
  const [info, setInfo] = useState(null);
  const [closing, setClosing] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { setClosing(false); onClose?.(); }, 320);
  };

  useEffect(() => {
    if (!open) return undefined;
    setClosing(false); setConfirmStop(false);
    const load = () => axios.get(`${API}/campagne/agente`).then((r) => setInfo(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [open]);

  const imposta = async (attivo) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/campagne/agente`, { attivo });
      setInfo((x) => ({ ...(x || {}), attivo: !!r.data?.attivo }));
      toast.info(attivo ? 'AI Campagne attivato' : 'AI Campagne fermato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setBusy(false); setConfirmStop(false);
  };

  const apriPagina = () => { onClose?.(); navigate('/campagne'); };

  if (!open) return null;

  const st = statoAgente(info);
  const motivo = motivoAgente(info);
  const lavora = st.chiave === 'attivo';
  const cifre = [
    ['Righe lavorate', nf(info?.lavorate_24h), 'ultime 24 ore'],
    ['Numeri ricostruiti', nf(info?.ricostruiti_24h), 'ultime 24 ore'],
    ['Resa', fmtResaAgente(info?.resa), 'ricostruiti su lavorate'],
    ['Velocità', fmtVelocita(info?.velocita_ora), 'solo mentre lavora'],
    ['Righe accese da fare', nf(info?.restano), 'di tutte le campagne'],
    ['Tempo stimato', fmtEta(info?.eta_minuti), 'alla velocità di oggi'],
  ];

  return createPortal(
    <div className={`bulk-overlay${closing ? ' closing' : ''}`}>
      <div className="bulk-dialog">
        <div className="bulk-bg">
          <div className="bulk-grid" />
          {lavora && (
            <div className="bulk-radar">
              <span className="bulk-ring r0" /><span className="bulk-ring r1" />
              <span className="bulk-ring r2" /><span className="bulk-ring r3" />
            </div>
          )}
        </div>

        <div className="bulk-content" style={{ gap: 12 }}>
          <div className="bulk-head">
            <span className="bulk-title">AI Da qualificare · righe accese</span>
            <button className="bulk-x" onClick={handleClose} aria-label="Chiudi"><IconClose size={16} color="#fff" /></button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
            <span style={{ width: 46, height: 46, borderRadius: 12, display: 'grid', placeItems: 'center',
              background: `color-mix(in srgb, ${st.colore} 22%, transparent)`, flex: '0 0 auto' }}>
              <IconCampagne size={26} color={st.chiave === 'spento' ? 'rgba(255,255,255,.55)' : st.colore} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>
                Agente <span style={{ color: st.chiave === 'spento' ? 'rgba(255,255,255,.6)' : st.colore }}>{st.etichetta}</span>
              </div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lavora && info?.corrente
                  ? <>sto guardando: <b style={{ color: '#fff' }}>{info.corrente}</b></>
                  : st.chiave === 'spento' ? 'Acceso, lavora da solo le righe accese di tutte le campagne.'
                    : lavora ? 'preparazione…' : ''}
              </div>
            </div>
          </div>

          {motivo && (
            <div style={{ width: '100%', borderRadius: 10, padding: '9px 12px', fontSize: 12.5, lineHeight: 1.45,
              background: `color-mix(in srgb, ${st.colore} 18%, transparent)`, color: '#fff' }}>
              {motivo}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: 8, width: '100%' }}>
            {cifre.map(([etichetta, valore, nota]) => (
              <div key={etichetta} className="bulk-stat" style={{ padding: '10px 8px' }}>
                <div className="v" style={{ fontSize: 19 }}>{valore}</div>
                <div className="l">{etichetta}</div>
                <div style={{ fontSize: 10, opacity: 0.5, marginTop: 2 }}>{nota}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, width: '100%', flexWrap: 'wrap' }}>
            <button type="button" className="bulk-keep" style={{ flex: '1 1 160px' }} onClick={apriPagina}>
              Apri la pagina Da qualificare
            </button>
            {st.chiave === 'spento' ? (
              <button type="button" className="bulk-keep" disabled={busy}
                style={{ flex: '1 1 160px', background: COLORE_CAMPAGNE }} onClick={() => imposta(true)}>
                {busy ? '…' : 'Accendi l’agente'}
              </button>
            ) : !confirmStop && (
              <button type="button" className="bulk-cancel" style={{ flex: '1 1 160px', marginTop: 0, width: 'auto' }}
                onClick={() => setConfirmStop(true)}>
                Ferma agente
              </button>
            )}
          </div>
          {confirmStop && (
            <div className="bulk-confirm">
              <span className="bulk-confirm-q">Fermare l’agente Da qualificare? La riga in corso viene finita.</span>
              <div className="bulk-confirm-btns">
                <button className="bulk-cancel" onClick={() => imposta(false)} disabled={busy}>{busy ? '…' : 'Sì'}</button>
                <button className="bulk-keep" onClick={() => setConfirmStop(false)} disabled={busy}>No</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
